import { useMemo } from "react";
import type { ID, printOnDemand } from "common/utils/models";
import type { CompositionForm } from "@design-lab/shared/types";
import type { UseQueryResult } from "@tanstack/react-query";
import { CompositionSizeGrid } from "./CompositionSizeGrid";

// Using the type directly to avoid extension issues
type Composition = printOnDemand.getProduct.Composition;
type Product = printOnDemand.getProduct.Response;

type CompositionsListProps = {
  formCompositions: CompositionForm[];
  compositions: Composition[];
  products: UseQueryResult<Product | undefined, Error>[];
  onRemoveComposition: (compositionId: ID) => void;
};

export function CompositionsList({
  formCompositions,
  compositions,
  products,
  onRemoveComposition
}: CompositionsListProps) {
  // Create lookup maps to avoid repeated find operations
  const compositionsMap = useMemo(
    () =>
      compositions.reduce(
        (acc, composition) => {
          acc[composition.id] = composition;
          return acc;
        },
        {} as Record<ID, Composition>
      ),
    [compositions]
  );

  const productsMap = useMemo(
    () =>
      products.reduce(
        (acc, productQuery) => {
          if (productQuery.data?.catalogNumber) {
            acc[productQuery.data.catalogNumber] = productQuery;
          }
          return acc;
        },
        {} as Record<string, UseQueryResult<Product | undefined, Error>>
      ),
    [products]
  );

  if (!formCompositions.length) {
    return <div className="py-4 text-center">No compositions added yet</div>;
  }

  return (
    <>
      {formCompositions.map(formComposition => {
        const composition = compositionsMap[formComposition.id];
        if (!composition) return null;

        // Find the product for this composition based on catalogId
        const productQuery = productsMap[formComposition.catalogId];
        const product = productQuery?.data;

        if (!product) return null;

        return (
          <div className="space-y-4" key={composition.id}>
            <CompositionSizeGrid
              productName={product.name}
              brand={product.brand}
              brandLogo={product.brandLogo}
              catalogId={formComposition.catalogId}
              onRemoveComposition={() => onRemoveComposition(composition.id)}
              composition={composition}
              showRemoveButton={formCompositions.length > 1}
            />
          </div>
        );
      })}
    </>
  );
}
